<script setup lang="ts">
import { computed } from "vue";
import { UiButton, UiIcon, UiText } from "@shopexperts/shared";
import type { UiFileUploadItem } from "@shopexperts/shared/dist/types/ui/UiFileUpload.vue";
/** @ts-ignore */
import { Container, Draggable } from "vue3-smooth-dnd";

const props = defineProps<{
    modelValue: UiFileUploadItem[];
    title?: string;
}>();

const emit = defineEmits<{
    (e: "update:modelValue", value: UiFileUploadItem[]): void;
    (e: "delete", item: UiFileUploadItem): void;
}>();

const items = computed({
    get: () => props.modelValue || [],
    set: (value: UiFileUploadItem[]) => emit("update:modelValue", value),
});

function getItemKey(item: UiFileUploadItem, index: number): string {
    if (item.media_id) return `media-${item.media_id}`;
    if (item.url) return `url-${item.url}`;
    if (item.file) return `file-${item.file.name}-${item.file.size}-${(item.file as File & { lastModified?: number }).lastModified ?? index}`;
    return `item-${index}`;
}

function getItemName(item: UiFileUploadItem, index: number): string {
    const originalName = typeof item.metadata?.original_name === "string"
        ? item.metadata.original_name
        : undefined;
    if (originalName) return originalName;

    if (item.filename) return item.filename;
    if (item.file?.name) return item.file.name;
    if (item.url) {
        const value = item.url.split("/").pop()?.split("?")[0];
        return value || `Image ${index + 1}`;
    }
    return `Image ${index + 1}`;
}

function onDrop(dropResult: { removedIndex: number | null; addedIndex: number | null }) {
    if (dropResult.removedIndex === null || dropResult.addedIndex === null) return;

    const nextItems = [...items.value];
    const [removed] = nextItems.splice(dropResult.removedIndex, 1);
    if (!removed) return;
    nextItems.splice(dropResult.addedIndex, 0, removed);
    items.value = nextItems;
}

function onDelete(item: UiFileUploadItem) {
    emit("delete", item);
}
</script>

<template>
    <div v-if="items.length" class="flex flex-col gap-2">
        <UiText as="p" variant="h6" class="font-semibold text-se-dark" :text="title || 'Uploaded Images'" />

        <Container
            drag-handle-selector=".drag-handle"
            drag-class="dragging"
            :animation-duration="300"
            class="flex flex-col gap-2"
            @drop="onDrop"
        >
            <Draggable
                v-for="(item, index) in items"
                :key="getItemKey(item, index)"
            >
                <div class="relative flex min-h-14 items-center rounded-md ring-inset ring-1 ring-se-gray-300 bg-white p-1">
                    <div class="flex min-w-0 flex-1 items-center gap-3">
                        <div class="h-12 w-[85px] overflow-hidden rounded-md border border-se-gray-300 bg-se-gray-50">
                            <img
                                v-if="item.url"
                                :src="item.url"
                                class="h-full w-full object-cover"
                            />
                            <div v-else class="flex h-full w-full items-center justify-center">
                                <UiIcon name="image-placeholder" class="h-5 w-5 text-se-gray-700" />
                            </div>
                        </div>
                        <UiText as="p" variant="small" class="min-w-0 truncate text-se-dark">
                            {{ getItemName(item, index) }}
                        </UiText>
                    </div>

                    <div class="flex items-center self-stretch">
                        <UiButton
                            variant="outline"
                            size="sm"
                            label="Delete"
                            class="mx-3"
                            @click="onDelete(item)"
                        >
                            <template #before_label>
                                <UiIcon name="delete" class="h-4 w-4 text-red-500" />
                            </template>
                        </UiButton>
                        <div class="drag-handle flex h-full w-6 cursor-grab items-center justify-center self-stretch border-l border-se-gray-300 active:cursor-grabbing">
                            <UiIcon name="drag-handle" class=" text-se-gray-500" />
                        </div>
                    </div>
                </div>
            </Draggable>
        </Container>
    </div>
</template>

<style scoped>
.dragging {
    opacity: 0.5;
    transform: rotate(1deg);
    cursor: grabbing !important;
}
</style>
